import React, { useEffect, useState } from "react";
import api from "../api";
import { Link, useNavigate } from "react-router-dom";
import ProductFilter from "./Products/modules/ProductFilter";
import ProductFilterV from "./Products/modules/ProductFilterV";

const SearchPage = ({ handleCart, handleWishlist }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loginData, setLoginData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [filters, setSelectedFilters] = useState({});
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const search = params.get("query");
        if (search) setQuery(search);
    }, []);

    useEffect(() => {
        const userData = sessionStorage.getItem("user_data");
        if (userData) {
            try {
                const parsedData = JSON.parse(userData);
                setLoginData(parsedData);
            } catch {
                setLoginData(null);
            }
        }
    }, []);

    useEffect(() => {
        const debounce = setTimeout(() => {
            const fetchProducts = async () => {
                if (!query || query.trim().length < 3) {
                    setShowResults(false);
                    setResults([]);
                    return;
                }

                setLoading(true);
                setShowResults(false);

                try {
                    const queryParams = new URLSearchParams({
                        name: query,
                        page: currentPage,
                        availability: filters.Availability || "",
                        price: filters.Price || "",
                        advanced_filters: filters.Advanced || "",
                    });

                    if (filters.Material)
                        queryParams.append("selected_attribute_values[]", `Material:${filters.Material}`);
                    if (filters.Size)
                        queryParams.append("selected_attribute_values[]", `Size:${filters.Size}`);
                    if (filters.Assembly)
                        queryParams.append("selected_attribute_values[]", `Assembly:${filters.Assembly}`);

                    const response = await api.get(`/products/search?${queryParams}`);
                    const data = response.data;

                    if (data && data.meta) {
                        setResults(data.data || []);
                        setLastPage(data.meta.last_page || 1);
                        setShowResults(true);
                    } else {
                        setResults([]);
                        setLastPage(1);
                        setShowResults(false);
                        setError(new Error("Invalid API response format"));
                    }
                } catch (err) {
                    setError(err);
                    setShowResults(false);
                } finally {
                    setLoading(false);
                }
            };

            fetchProducts();
        }, 500);

        return () => clearTimeout(debounce);
    }, [query, currentPage, filters]);

    const handleFilterChange = (newFilters) => {
        setSelectedFilters(newFilters);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= lastPage) {
            setCurrentPage(page);
        }
    };

    useEffect(() => {
        const fetchCategoriesAndSubcategories = async () => {
            try {
                const response = await api.get("/categories");
                const fetchedCategories = response.data.data;

                // Fetch subcategories for each category
                const categoriesWithSubcategories = await Promise.all(
                    fetchedCategories.map(async (category) => {
                        const subResponse = await api.get(`/sub-categories/${category.id}`);
                        return {
                            ...category,
                            subcategories: subResponse.data.data,
                        };
                    })
                );

                setCategories(categoriesWithSubcategories);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchCategoriesAndSubcategories();
    }, []);

    const handleSelect = (e) => {
        const value = e.target.value;
        if (value) {
            navigate(value);
        }
    };

    return (
        <div className="container py-5">
            <h5 className="mb-4">
                Search Results for: <strong>{query}</strong>
                <p>{showResults && ` (${results.length} ${results.length === 1 ? 'product' : 'products'})`}</p>
            </h5>

            <div className="row">
                {/* Filters Column */}
                <div className="col-lg-3 mb-4">
                    {/* Category Dropdown */}
                    <div className="border p-3 bg-light sticky-filter mb-3">
                        <h5 className="mb-2">Categories</h5>

                        {loading ? (
                            <p>Loading...</p>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                <select className="form-select" onChange={handleSelect} defaultValue="">
                                    <option value="" disabled>
                                        Select a category
                                    </option>
                                    {categories.map((cat) => (
                                        <React.Fragment key={cat.id}>
                                            <option value={`/category/${cat.slug}`}>{cat.name}</option>
                                            {cat.subcategories.map((sub) => (
                                                <option key={sub.id} value={`/category/${sub.slug}`}>
                                                    &nbsp;&nbsp;↳ {sub.name}
                                                </option>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>


                    {/* Filters */}
                    <div className="border p-3 bg-light sticky-filter">
                        <h5 className="mb-2">Filters</h5>
                        <ProductFilterV onFilterChange={handleFilterChange} onResetFilters={() => handleFilterChange({})}
                        />
                    </div>
                </div>

                {/* Product Grid Column */}
                <div className="col-lg-9">
                    {loading && <p>Loading...</p>}

                    {!loading && showResults && results.length === 0 && (
                        <p>No products found.</p>
                    )}

                    {!loading && !showResults && (
                        <p>Enter at least 3 characters to search.</p>
                    )}

                    <div className="row">
                        {results.map((product) => (
                            <div className="product-block col-lg-4 col-md-6 col-sm-6 col-12" key={product.id}>
                                <div className="inner-box">
                                    <div className="image">
                                        <Link to={`/products/${product.slug}`}>
                                            <img src={product.thumbnail_image} alt={product.name} />
                                        </Link>
                                    </div>

                                    {product.discount !== 0 && (
                                        <div className="product-success">
                                            {product.discount}% Off
                                        </div>
                                    )}
                                    <div className="product-made">
                                        {product.stock_quantity === 0 ? (
                                            <span className="text-danger">Out Of Stock</span>
                                        ) : (
                                            <span>MADE TO ORDER</span>
                                        )}
                                    </div>
                                    <div className="content-box">
                                        <Link to={`/products/${product.slug}`} className="buy-btn">
                                            BUY NOW
                                        </Link>
                                        <Link
                                            to="#"
                                            onClick={() => handleCart(product.id, '', loginData?.id, 1)}
                                            className="cart-btn"
                                        >
                                            ADD TO CART
                                        </Link>
                                    </div>
                                    <div className="content">
                                        <h4>
                                            <Link to={`/products/${product.slug}`}>
                                                {product.name}
                                            </Link>
                                        </h4>
                                        <span className="price">
                                            {product.stocked_price !== product.main_price && (
                                                <del>{product.stocked_price}</del>
                                            )}
                                            &nbsp;{product.main_price}
                                        </span>
                                    </div>
                                    <div className="icon-box">
                                        {loginData ? (
                                            <Link
                                                to="#"
                                                onClick={() => handleWishlist(product.slug)}
                                                className="ui-btn like-btn"
                                                aria-label="Add to Wishlist"
                                            >
                                                <i className="fa fa-heart"></i>
                                            </Link>
                                        ) : (
                                            <Link
                                                to="/login"
                                                className="ui-btn add-to-cart"
                                                aria-label="Login to Add to Cart"
                                            >
                                                <i className="fa fa-heart"></i>
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination controls */}
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="project-details__pagination-box">
                                <ul className="project-details__pagination list-unstyled clearfix">
                                    <li className="previous">
                                        <div className="icon">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                aria-label="Previous"
                                            >
                                                <i className="lnr lnr-icon-arrow-left"></i>
                                            </button>
                                        </div>
                                    </li>
                                    <li className="count px-3">
                                        <span>Page {currentPage} of {lastPage}</span>
                                    </li>
                                    <li className="next">
                                        <div className="icon">
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === lastPage}
                                                aria-label="Next"
                                            >
                                                <i className="lnr lnr-icon-arrow-right"></i>
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchPage;
